import ArticleLink from '@/components/feature/ArticleLink';
import { articlePath, type Article, type Category } from '@/utils/content';
import CategoryOverviewCard, { type CategorySummary } from '@/pages/category/components/CategoryOverviewCard';

function buildSummary(
  articles: Article[],
  outcomes: { title: string; slug: string }[],
  audience: string[],
): CategorySummary {
  const levels = Array.from(new Set(articles.map((article) => article.level)));
  const allTags = Array.from(new Set(articles.flatMap((article) => article.tags)));
  const tags = allTags.slice(0, 8);

  return {
    articleCount: articles.length,
    level: levels.length > 0 ? levels.join(' · ') : 'เริ่มต้น',
    tags,
    topicCount: allTags.length,
    learnings: outcomes,
    audience,
  };
}

type CategoryHeroProps = {
  category: Category;
  articles: Article[];
};

export default function CategoryHero({ category, articles }: CategoryHeroProps) {
  const summary = buildSummary(articles, category.outcomes, category.audience);

  return (
    <section className="relative overflow-hidden border-b border-background-200 bg-background-50">
      <div className="pointer-events-none absolute inset-0 grid-lines opacity-50" />
      <div className="pointer-events-none absolute -right-24 -top-28 h-72 w-72 rounded-full bg-primary-100/60 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 left-1/4 h-64 w-64 rounded-full bg-accent-100/50 blur-3xl" />

      <div className="relative mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12 lg:px-10 lg:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px] lg:items-stretch lg:gap-12">
          <div className="flex min-w-0 flex-col">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className={category.icon} />
              {category.tagline}
            </span>

            <h1 className="mt-4 text-balance font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-4xl lg:text-5xl">
              {category.name}
            </h1>

            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {category.description}
            </p>

            <div className="mt-6 flex flex-1 flex-col rounded-lg border border-background-200 bg-background-100/40 px-4 py-3.5 md:px-5">
              <p className="flex items-center gap-2 text-xs font-semibold text-foreground-800">
                <i className="ri-lightbulb-flash-line text-base text-accent-600" />
                เรียนจบหมวดนี้แล้วจะทำอะไรได้
              </p>
              <p className="mt-1 text-xs text-foreground-500">
                ทักษะที่ได้จากการอ่านบทความในหมวดนี้ พร้อมลิงก์ไปบทที่เกี่ยวข้อง
              </p>
              <ul className="mt-2 flex flex-1 flex-col divide-y divide-background-200/80">
                {summary.learnings.map((item) => (
                  <li key={item.slug} className="flex flex-1">
                    <ArticleLink
                      to={articlePath(item.slug)}
                      className="group flex w-full items-center gap-3 py-2.5"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[11px] text-primary-700">
                        <i className="ri-check-line" />
                      </span>
                      <span className="min-w-0 flex-1 text-sm leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <i className="ri-arrow-right-up-line shrink-0 text-sm text-foreground-400 transition-colors group-hover:text-primary-700" />
                    </ArticleLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <CategoryOverviewCard summary={summary} />
        </div>
      </div>
    </section>
  );
}